import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleGoogleCallback, isAuthenticated } = useAuth();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double-processing in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      // Extract session_id from hash fragment (Emergent auth format: #session_id=xxx)
      let sessionId = null;
      
      if (location.hash) {
        const hashParams = new URLSearchParams(location.hash.substring(1));
        sessionId = hashParams.get('session_id');
      }
      
      // Fallback to window.location.hash in case React Router doesn't capture it
      if (!sessionId && window.location.hash) {
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        sessionId = params.get('session_id');
      }

      console.log('Auth callback processing...');
      console.log('Location hash:', location.hash);
      console.log('Window hash:', window.location.hash);
      console.log('Session ID:', sessionId);
      
      if (!sessionId) {
        setError('No session ID found. Please try logging in again.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        await handleGoogleCallback(sessionId);
        toast.success('Successfully logged in with Google!');
        // Clear the hash from URL before navigating
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/');
      } catch (err) {
        console.error('Google auth callback error:', err);
        setError(err.response?.data?.detail || 'Failed to complete login. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    if (!isAuthenticated) {
      processCallback();
    } else {
      navigate('/');
    }
  }, [location.hash, handleGoogleCallback, navigate, isAuthenticated]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full mx-4">
        <div className="mb-6">
          <h1 className="text-2xl font-outfit font-bold tracking-tighter text-slate-900">
            dxb<span className="text-[#0047AB]">2</span>bom
          </h1>
        </div>

        {error ? (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-600 font-medium">{error}</p>
            <p className="text-slate-500 text-sm">Redirecting to login...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <Loader2 className="w-12 h-12 mx-auto text-[#0047AB] animate-spin" />
            <p className="text-slate-600 font-medium">Completing your login...</p>
            <p className="text-slate-400 text-sm">Please wait a moment</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;
